import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchSkyleadLeadsByTag, fetchSkyleadConversation } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Search, MessageCircle, Linkedin, ExternalLink, Loader2, Tag as TagIcon } from "lucide-react";

const PRIORITY_TAGS = [
  "Interesado",
  "Coordinando reunión",
  "Reunión OK",
  "Contactar más adelante",
  "Por Fuera",
  "En proceso",
];

export default function ConversationsByTagSection({ clientId }) {
  const [activeTag, setActiveTag] = useState("Interesado");
  const [search, setSearch] = useState("");
  const [openLead, setOpenLead] = useState(null);

  const leadsQ = useQuery({
    queryKey: ["sk-leads-by-tag", clientId],
    queryFn: () => fetchSkyleadLeadsByTag(clientId, 30),
    staleTime: 5 * 60_000,
  });

  const { tags, leadsByTag } = useMemo(() => {
    const data = leadsQ.data;
    if (!data) return { tags: [], leadsByTag: {} };
    const grouped = {};
    for (const l of data.leads || []) {
      for (const t of l.tags || []) {
        (grouped[t] = grouped[t] || []).push(l);
      }
    }
    const distribution = data.tag_distribution || [];
    // Place priority tags first (if present), then the rest by count
    const priorityFound = PRIORITY_TAGS.filter((t) => grouped[t]).map((t) => ({ tag: t, count: grouped[t].length }));
    const rest = distribution.filter((d) => !PRIORITY_TAGS.includes(d.tag));
    return { tags: [...priorityFound, ...rest], leadsByTag: grouped };
  }, [leadsQ.data]);

  const filtered = useMemo(() => {
    const list = leadsByTag[activeTag] || [];
    if (!search) return list;
    const q = search.toLowerCase();
    return list.filter((l) =>
      (l.full_name || "").toLowerCase().includes(q) ||
      (l.company || "").toLowerCase().includes(q) ||
      (l.occupation || "").toLowerCase().includes(q)
    );
  }, [leadsByTag, activeTag, search]);

  return (
    <section className="bg-white border border-slate-200 rounded-md p-5 min-w-0 max-w-full overflow-hidden" data-testid="sk-conversations">
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div className="min-w-0">
          <h3 className="font-display text-sm font-semibold text-slate-900">Conversaciones por tag</h3>
          <p className="text-[11px] text-slate-500 mt-0.5">
            {leadsQ.isLoading ? "Escaneando leads…" : `${(leadsQ.data?.leads || []).length} leads etiquetados`}
            {leadsQ.data?.scanned_pages_per_seat ? ` · ${leadsQ.data.scanned_pages_per_seat} páginas escaneadas por perfil` : ""}
          </p>
        </div>
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
          <Input
            placeholder="Buscar nombre, empresa o cargo…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-9 pl-9 bg-white text-xs"
            data-testid="sk-conv-search"
          />
        </div>
      </div>

      {/* Tag chips */}
      <div className="flex items-center gap-1.5 flex-wrap mb-4" data-testid="sk-tag-chips">
        {leadsQ.isLoading ? (
          <div className="flex gap-2"><Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" /><span className="text-[11px] text-slate-500">Cargando tags…</span></div>
        ) : tags.length === 0 ? (
          <div className="text-[11px] text-slate-400 italic">Sin leads etiquetados encontrados.</div>
        ) : (
          tags.map((t) => (
            <button
              key={t.tag}
              onClick={() => setActiveTag(t.tag)}
              className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${
                activeTag === t.tag
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
              }`}
              data-testid={`sk-tag-${t.tag}`}
            >
              <TagIcon className="h-2.5 w-2.5 inline-block mr-1 -mt-0.5" />
              {t.tag} <span className={`ml-1 ${activeTag === t.tag ? "text-white/80" : "text-slate-400"}`}>{t.count}</span>
            </button>
          ))
        )}
      </div>

      {/* Lead list */}
      {leadsQ.isLoading ? (
        <div className="h-48 skeleton rounded-md" />
      ) : filtered.length === 0 ? (
        <div className="text-center py-8 text-xs text-slate-400 italic">No hay leads con esta combinación de filtros.</div>
      ) : (
        <div className="divide-y divide-slate-100 border border-slate-200 rounded-md max-h-[600px] overflow-y-auto" data-testid="sk-leads-list">
          {filtered.map((lead) => (
            <LeadRow key={`${lead.account_id}-${lead.lead_id}`} lead={lead} activeTag={activeTag} onOpen={() => setOpenLead(lead)} />
          ))}
        </div>
      )}

      <ConversationSheet
        clientId={clientId}
        lead={openLead}
        open={!!openLead}
        onOpenChange={(o) => !o && setOpenLead(null)}
      />
    </section>
  );
}

function LeadRow({ lead, activeTag, onOpen }) {
  const otherTags = (lead.tags || []).filter((t) => t !== activeTag);
  return (
    <button
      type="button"
      onClick={onOpen}
      className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50 text-left transition-colors"
      data-testid={`sk-lead-${lead.lead_id}`}
    >
      <div className="h-9 w-9 rounded-full bg-slate-100 overflow-hidden shrink-0">
        {lead.image ? (
          <img src={lead.image} alt={lead.full_name} className="h-full w-full object-cover" />
        ) : (
          <div className="h-full w-full flex items-center justify-center text-[10px] font-semibold text-slate-400">
            {(lead.first_name?.[0] || "?") + (lead.last_name?.[0] || "")}
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <span className="font-medium text-xs text-slate-900 truncate">{lead.full_name}</span>
          {lead.linkedin_url && (
            <Linkedin className="h-3 w-3 text-[#0A66C2] shrink-0" />
          )}
        </div>
        <div className="text-[11px] text-slate-500 truncate" title={`${lead.occupation} · ${lead.company}`}>
          <span className="text-slate-700">{lead.occupation || "—"}</span>
          {lead.company && <> · {lead.company}</>}
        </div>
        {otherTags.length > 0 && (
          <div className="flex items-center gap-1 mt-1 flex-wrap">
            {otherTags.slice(0, 3).map((t) => (
              <span key={t} className="text-[9px] px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded-sm">{t}</span>
            ))}
          </div>
        )}
      </div>
      <div className="text-[10px] text-slate-400 shrink-0 flex items-center gap-1">
        <span className="hidden md:inline">{lead.profile_name}</span>
        <MessageCircle className="h-3.5 w-3.5" />
      </div>
    </button>
  );
}

function ConversationSheet({ clientId, lead, open, onOpenChange }) {
  const convQ = useQuery({
    queryKey: ["sk-conversation", clientId, lead?.account_id, lead?.thread],
    queryFn: () => fetchSkyleadConversation(clientId, lead.account_id, lead.thread),
    enabled: !!(open && lead?.thread && lead?.account_id),
    staleTime: 60_000,
  });

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-[560px] overflow-y-auto p-0">
        {lead && (
          <>
            <SheetHeader className="px-5 pt-5 pb-3 border-b border-slate-200 sticky top-0 bg-white z-10">
              <div className="flex items-start gap-3">
                <div className="h-12 w-12 rounded-full bg-slate-100 overflow-hidden shrink-0">
                  {lead.image ? <img src={lead.image} alt="" className="h-full w-full object-cover" /> : <div className="h-full w-full flex items-center justify-center text-xs text-slate-400">{(lead.first_name?.[0] || "?") + (lead.last_name?.[0] || "")}</div>}
                </div>
                <div className="flex-1 min-w-0">
                  <SheetTitle className="text-base text-slate-900 truncate">{lead.full_name}</SheetTitle>
                  <SheetDescription className="text-[11px] text-slate-500 leading-tight">
                    {lead.occupation}
                    {lead.company && <span className="block">{lead.company}</span>}
                  </SheetDescription>
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    {(lead.tags || []).map((t) => (
                      <span key={t} className="text-[10px] px-1.5 py-0.5 bg-indigo-50 text-indigo-700 rounded-sm font-medium border border-indigo-200">{t}</span>
                    ))}
                  </div>
                  {lead.linkedin_url && (
                    <a href={lead.linkedin_url} target="_blank" rel="noreferrer" className="text-[11px] text-[#0A66C2] hover:underline inline-flex items-center gap-1 mt-2">
                      <Linkedin className="h-3 w-3" /> Ver en LinkedIn <ExternalLink className="h-2.5 w-2.5" />
                    </a>
                  )}
                </div>
              </div>
            </SheetHeader>
            <div className="px-5 py-4 space-y-2" data-testid="sk-conv-messages">
              {convQ.isLoading ? (
                <div className="space-y-2">
                  <div className="h-12 skeleton rounded-md" />
                  <div className="h-16 skeleton rounded-md ml-12" />
                  <div className="h-12 skeleton rounded-md" />
                </div>
              ) : convQ.error ? (
                <p className="text-xs text-red-600">Error al cargar la conversación.</p>
              ) : (convQ.data?.messages || []).length === 0 ? (
                <p className="text-xs text-slate-400 italic text-center py-8">Sin mensajes registrados.</p>
              ) : (
                (convQ.data.messages || []).map((m) => <Bubble key={m.id} m={m} />)
              )}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Bubble({ m }) {
  const outgoing = m.direction === "outgoing";
  const time = m.created_at
    ? new Date(m.created_at).toLocaleString("es-AR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })
    : "";
  const text = htmlToText(m.message || "");
  return (
    <div className={`flex ${outgoing ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[85%] rounded-lg px-3 py-2 text-xs ${
        outgoing
          ? "bg-indigo-600 text-white rounded-br-sm"
          : "bg-slate-100 text-slate-900 rounded-bl-sm"
      }`}>
        <div className="whitespace-pre-wrap break-words leading-snug">{text}</div>
        <div className={`text-[9px] mt-1 ${outgoing ? "text-indigo-200" : "text-slate-500"}`}>
          {m.message_type === "EMAIL" ? "Email · " : ""}{time}
        </div>
      </div>
    </div>
  );
}

function htmlToText(raw) {
  if (!raw) return "";
  // If clearly not HTML, return as-is
  if (!/<\s*(html|body|div|p|br|span|table)\b/i.test(raw)) return raw;
  // Replace common block tags with newlines, then strip the rest
  let s = raw
    .replace(/<\s*br\s*\/?\s*>/gi, "\n")
    .replace(/<\/\s*(p|div|tr|li|h[1-6])\s*>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
  return s;
}
